import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './ObjectFilterPanel.module.css';
import { CloseIcon, SearchIcon, BoundaryIcon, BuildingIcon, RoadIcon, PinIcon, ForSaleIcon, UnderConstructionIcon, PlannedIcon } from '../Icon';
import Button from '../Button/Button';

const ObjectFilterPanel = ({ isVisible, onClose, onApplyFilters, initialFilters, allFeatures, legendData }) => {
    const [filters, setFilters] = useState(initialFilters);
    const [ownerSuggestions, setOwnerSuggestions] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({
        plots: true,
        buildings: true,
        roads: true,
        markers: true,
    });
    const [showOwnerSuggestions, setShowOwnerSuggestions] = useState(false);

    const allOwners = useMemo(() => {
        if (!allFeatures) return [];
        const owners = new Set();
        allFeatures.forEach(f => {
            if (f.properties.owner) {
                owners.add(f.properties.owner);
            }
        });
        return Array.from(owners).sort();
    }, [allFeatures]);

    const availableCategories = useMemo(() => {
        if (!legendData) return {};
        return {
            plots: legendData.plots || [],
            buildings: legendData.buildings || [],
            roads: legendData.roads || [],
            markers: legendData.markers || [],
        }
    }, [legendData]);

    // Синхронизация внутренних состояний с initialFilters при изменении
    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    const isInitialRender = useRef(true);
    // Мгновенное применение фильтров при любом изменении
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        if (filters) { // Применяем только если фильтры определены
            onApplyFilters(filters);
        }
    }, [filters]);

    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleStatusToggle = (status) => {
        const newStatuses = (activeStatuses || []).includes(status)
            ? activeStatuses.filter(s => s !== status)
            : [...activeStatuses, status];
        updateFilter('activeStatuses', newStatuses);
    };

    const handleCategoryToggle = (categoryId) => {
        const newCategories = (filters.activeCategories || []).includes(categoryId)
            ? filters.activeCategories.filter(c => c !== categoryId)
            : [...filters.activeCategories, categoryId];
        updateFilter('activeCategories', newCategories);
    };

    const handleCategorySectionToggle = (type) => {
        setExpandedCategories(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const handleSelectAllCategoriesForType = (type, shouldSelect) => {
        const categoryIdsForType = (availableCategories[type] || []).map(cat => cat.id);
        let currentCategories = filters.activeCategories || [];

        if (shouldSelect) {
            // Добавляем все ID этого типа, избегая дубликатов
            currentCategories = [...new Set([...currentCategories, ...categoryIdsForType])];
        } else {
            // Удаляем все ID этого типа
            const typeIdsSet = new Set(categoryIdsForType);
            currentCategories = currentCategories.filter(id => !typeIdsSet.has(id));
        }
        updateFilter('activeCategories', currentCategories);
    };

    const areAllCategoriesSelectedForType = (type) => {
        const categoryIdsForType = (availableCategories[type] || []).map(cat => cat.id);
        return categoryIdsForType.every(id => (filters.activeCategories || []).includes(id));
    };

    const handleOwnerChange = (e) => {
        const value = e.target.value;
        updateFilter('ownerSearch', value);
        if (value) {
            setOwnerSuggestions(allOwners.filter(owner => owner.toLowerCase().includes(value.toLowerCase())));
            setShowOwnerSuggestions(true);
        } else {
            setOwnerSuggestions([]);
            setShowOwnerSuggestions(false);
        }
    };

    const handleOwnerSelect = (owner) => {
        updateFilter('ownerSearch', owner);
        setShowOwnerSuggestions(false);
    };

    const handleClear = () => onApplyFilters({}); // Сбрасываем на пустой объект

    if (!isVisible) return null;

    const { searchTerm, activeTypes, minArea, maxArea, ownerSearch, minHeight, maxHeight, hasTenants, hasMedia, hasDescription, activeStatuses, activeCategories, noCategory } = filters || {};

    return (
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
                <h3>Расширенный фильтр</h3>
                <button onClick={onClose} className={styles.closeButton}><CloseIcon size={20} /></button>
            </div>
            <div className={styles.content}>
                <div className={styles.section}>
                    <h4>Поиск по тексту</h4>
                    <div className={styles.searchContainer}>
                        <SearchIcon className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Поиск по названию, ID, кадастровому номеру, описанию..."
                            value={searchTerm || ''}
                            onChange={(e) => updateFilter('searchTerm', e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Категории</h4>
                    <div className={styles.categoryAccordion}>
                        <label className={`${styles.categoryItem} ${styles.noCategoryItem}`}>
                            <input type="checkbox" checked={!!noCategory} onChange={e => updateFilter('noCategory', e.target.checked)} />
                            Без категории
                        </label>

                        {availableCategories.plots.length > 0 && (
                            <div className={styles.categorySection}>
                                <div className={styles.categoryHeader} onClick={() => handleCategorySectionToggle('plots')}>
                                    <span className={`${styles.accordionArrow} ${expandedCategories.plots ? styles.expanded : ''}`}></span>
                                    <input type="checkbox" checked={areAllCategoriesSelectedForType('plots')} onChange={(e) => handleSelectAllCategoriesForType('plots', e.target.checked)} />
                                    <h5>Участки</h5>
                                </div>
                                {expandedCategories.plots && (
                                    <div className={styles.categoryList}>
                                        {availableCategories.plots.map(cat => <label key={cat.id} className={styles.categoryItem}><input type="checkbox" checked={activeCategories?.includes(cat.id)} onChange={() => handleCategoryToggle(cat.id)} /> <span className={styles.colorSwatch} style={{ backgroundColor: cat.color }}></span> {cat.name}</label>)}
                                    </div>
                                )}
                            </div>
                        )}
                        {availableCategories.buildings.length > 0 && (
                            <div className={styles.categorySection}>
                                <div className={styles.categoryHeader} onClick={() => handleCategorySectionToggle('buildings')}>
                                    <span className={`${styles.accordionArrow} ${expandedCategories.buildings ? styles.expanded : ''}`}></span>
                                    <input type="checkbox" checked={areAllCategoriesSelectedForType('buildings')} onChange={(e) => handleSelectAllCategoriesForType('buildings', e.target.checked)} />
                                    <h5>Здания</h5>
                                </div>
                                {expandedCategories.buildings && (
                                    <div className={styles.categoryList}>
                                        {availableCategories.buildings.map(cat => <label key={cat.id} className={styles.categoryItem}><input type="checkbox" checked={activeCategories?.includes(cat.id)} onChange={() => handleCategoryToggle(cat.id)} /> <span className={styles.colorSwatch} style={{ backgroundColor: cat.color }}></span> {cat.name}</label>)}
                                    </div>
                                )}
                            </div>
                        )}
                        {availableCategories.roads.length > 0 && (
                             <div className={styles.categorySection}>
                                <div className={styles.categoryHeader} onClick={() => handleCategorySectionToggle('roads')}>
                                    <span className={`${styles.accordionArrow} ${expandedCategories.roads ? styles.expanded : ''}`}></span>
                                    <input type="checkbox" checked={areAllCategoriesSelectedForType('roads')} onChange={(e) => handleSelectAllCategoriesForType('roads', e.target.checked)} />
                                    <h5>Линии</h5>
                                </div>
                                {expandedCategories.roads && (
                                    <div className={styles.categoryList}>
                                        {availableCategories.roads.map(cat => <label key={cat.id} className={styles.categoryItem}><input type="checkbox" checked={activeCategories?.includes(cat.id)} onChange={() => handleCategoryToggle(cat.id)} /> <span className={styles.colorSwatch} style={{ backgroundColor: cat.color }}></span> {cat.name}</label>)}
                                    </div>
                                )}
                            </div>
                        )}
                        {availableCategories.markers.length > 0 && (
                             <div className={styles.categorySection}>
                                <div className={styles.categoryHeader} onClick={() => handleCategorySectionToggle('markers')}>
                                    <span className={`${styles.accordionArrow} ${expandedCategories.markers ? styles.expanded : ''}`}></span>
                                    <input type="checkbox" checked={areAllCategoriesSelectedForType('markers')} onChange={(e) => handleSelectAllCategoriesForType('markers', e.target.checked)} />
                                    <h5>Метки</h5>
                                </div>
                                 {expandedCategories.markers && (
                                     <div className={styles.categoryList}>
                                         {availableCategories.markers
                                             .filter(cat => cat.image_url) // Показываем только метки с изображениями
                                             .map(cat => (
                                                 <label key={cat.id} className={styles.categoryItem}>
                                                     <input type="checkbox" checked={activeCategories?.includes(cat.id)} onChange={() => handleCategoryToggle(cat.id)} />
                                                     <img 
                                                        src={cat.image_url} 
                                                        className={styles.markerIcon} 
                                                        alt={cat.name}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                     /> 
                                                     {cat.name}
                                                 </label>
                                             ))
                                         }
                                     </div>
                                 )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Статусы</h4>
                    <div className={styles.statusFilterButtons}>
                        <button
                            className={`${styles.filterButton} ${activeStatuses?.includes('for_sale') ? styles.active : ''}`}
                            onClick={() => handleStatusToggle('for_sale')}
                        >
                            <ForSaleIcon size={16} /> В продаже
                        </button>
                        <button
                            className={`${styles.filterButton} ${activeStatuses?.includes('is_building') ? styles.active : ''}`}
                            onClick={() => handleStatusToggle('is_building')}
                        >
                            <UnderConstructionIcon size={16} /> В строительстве
                        </button>
                        <button
                            className={`${styles.filterButton} ${activeStatuses?.includes('is_planned') ? styles.active : ''}`}
                            onClick={() => handleStatusToggle('is_planned')}
                        >
                            <PlannedIcon size={16} /> Запланировано
                        </button>
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Владелец</h4>
                    <div className={styles.ownerContainer}>
                        <input
                            type="text"
                            placeholder="Поиск по владельцу"
                            value={ownerSearch || ''}
                            onChange={handleOwnerChange}
                            onFocus={() => setShowOwnerSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowOwnerSuggestions(false), 150)}
                            className={styles.searchInput}
                        />
                        {showOwnerSuggestions && ownerSuggestions.length > 0 && (
                            <ul className={styles.suggestionsList}>
                                {ownerSuggestions.map(owner => (
                                    <li key={owner} onMouseDown={() => handleOwnerSelect(owner)}>
                                        {owner}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Площадь (м²)</h4>
                    <div className={styles.areaFilter}>
                        <input
                            type="number"
                            placeholder="От"
                            value={minArea || ''}
                            onChange={(e) => updateFilter('minArea', e.target.value)}
                            className={styles.areaInput}
                        />
                        <span>—</span>
                        <input
                            type="number"
                            placeholder="До"
                            value={maxArea || ''}
                            onChange={(e) => updateFilter('maxArea', e.target.value)}
                            className={styles.areaInput}
                        />
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Высота (м)</h4>
                    <div className={styles.areaFilter}>
                        <input
                            type="number"
                            placeholder="От"
                            value={minHeight || ''}
                            onChange={(e) => updateFilter('minHeight', e.target.value)}
                            className={styles.areaInput}
                        />
                        <span>—</span>
                        <input
                            type="number"
                            placeholder="До"
                            value={maxHeight || ''}
                            onChange={(e) => updateFilter('maxHeight', e.target.value)}
                            className={styles.areaInput}
                        />
                    </div>
                </div>

                <div className={styles.section}>
                    <h4>Прочее</h4>
                    <div className={styles.checkboxGroup}>
                        <label><input type="checkbox" checked={!!hasTenants} onChange={e => updateFilter('hasTenants', e.target.checked)} /> Есть арендаторы</label>
                        <label><input type="checkbox" checked={!!hasMedia} onChange={e => updateFilter('hasMedia', e.target.checked)} /> Есть медиа (фото/ссылки)</label>
                        <label><input type="checkbox" checked={!!hasDescription} onChange={e => updateFilter('hasDescription', e.target.checked)} /> Есть описание</label>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <Button variant="default" onClick={handleClear}>Сбросить все фильтры</Button>
            </div>
        </div>
    );
};

export default ObjectFilterPanel;